import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { BrandStorefrontService } from '../../services/brand-storefront.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';

@Component({
	selector: 'app-brand-storefronts',
	templateUrl: './brand-storefronts.component.html',
	styleUrls: ['./brand-storefronts.component.scss']
})
export class BrandStorefrontsComponent implements OnInit {
	color = 'blue';
	rows: any[] = [];
	filteredRows: any[] = [];
	search = '';
	loading = false;
	saving = false;
	billing = false;
	selected: any = null;
	form: FormGroup;
	message = '';
	messageType: 'success' | 'danger' | '' = '';
	copyHint = '';

	// Merchandising: drag the brand list into the order it appears on the brand
	// wall and the home strip. Off by default so the list stays click-to-edit —
	// rows are selectable, and draggable rows that also select are confusing.
	reorder = false;
	reorderDirty = false;

	constructor(
		private location: Location,
		private fb: FormBuilder,
		private brandStorefrontService: BrandStorefrontService,
		private globalSearchService: GlobalSearchService
	) {
		this.color = this.globalSearchService.getColor();
		this.form = this.fb.group({
			manufacturer_id: [0, Validators.required],
			enabled: [false],
			status: ['cancelled'],
			monthly_fee: [100, [Validators.required, Validators.min(0)]],
			setup_fee: [0, [Validators.min(0)]],
			bill_to_debtorno: [''],
			bill_to_branchcode: [''],
			slug: [''],
			// Contact details live on the manufacturer row. contact_email is where
			// brand order notifications are sent, so a stale one silently drops them.
			contact_email: ['', [Validators.email]],
			website_url: [''],
			welcome_text: [''],
			hero_image: [''],
			banner_image: [''],
			promo_banner_text: [''],
			promo_banner_link: [''],
			accent_color: [''],
			notes: [''],
			invoice_setup_fee: [true]
		});
	}

	ngOnInit(): void {
		this.loadRows();
		this.form.get('enabled')?.valueChanges.subscribe(() => this.updateBillToValidators());
		this.form.get('status')?.valueChanges.subscribe(() => this.updateBillToValidators());
	}

	updateBillToValidators(): void {
		const enabled = this.form.get('enabled')?.value;
		const status = this.form.get('status')?.value;
		const billCtrl = this.form.get('bill_to_debtorno');
		if (enabled && status === 'active') {
			billCtrl?.setValidators([Validators.required]);
		} else {
			billCtrl?.clearValidators();
		}
		billCtrl?.updateValueAndValidity({ emitEvent: false });
	}

	loadRows(): void {
		this.loading = true;
		this.message = '';
		this.brandStorefrontService.list().subscribe((res: any) => {
			this.loading = false;
			this.rows = res?.rows || [];
			this.applyFilter();
			if (this.selected) {
				const refreshed = this.rows.find(r => r.manufacturer_id === this.selected.manufacturer_id);
				if (refreshed) {
					this.selectRow(refreshed, false);
				}
			}
		}, () => {
			this.loading = false;
			this.showMessage('Unable to load brand storefronts.', 'danger');
		});
	}

	/** Brand-wall order: sort_order ascending, name as the tiebreak — the same
	 *  rule product/manufacturer.php applies when it renders the grid. */
	private byWallOrder(a: any, b: any): number {
		const av = Number(a?.sort_order ?? 0);
		const bv = Number(b?.sort_order ?? 0);
		if (av !== bv) { return av - bv; }
		return String(a?.brand_name || '').localeCompare(String(b?.brand_name || ''));
	}

	toggleReorder(): void {
		this.reorder = !this.reorder;
		if (this.reorder) {
			// Dragging a filtered subset would write positions for rows the user
			// cannot see, so reordering always works on the full list.
			this.search = '';
			this.applyFilter();
		} else if (this.reorderDirty) {
			this.reorderDirty = false;
			this.loadRows();
		}
	}

	drop(event: CdkDragDrop<any[]>): void {
		if (event.previousIndex === event.currentIndex) { return; }
		moveItemInArray(this.filteredRows, event.previousIndex, event.currentIndex);
		this.reorderDirty = true;
	}

	saveOrder(): void {
		this.saving = true;
		this.message = '';
		const ids = this.filteredRows.map(r => r.manufacturer_id);
		this.brandStorefrontService.reorder(ids).subscribe(() => {
			this.saving = false;
			this.reorderDirty = false;
			this.showMessage('Brand order saved.', 'success');
			this.loadRows();
		}, () => {
			this.saving = false;
			this.showMessage('Could not save the brand order.', 'danger');
		});
	}

	applyFilter(): void {
		const q = this.search.trim().toLowerCase();
		const base = !q
			? this.rows.slice()
			: this.rows.filter(r =>
				(r.brand_name || '').toLowerCase().includes(q)
				|| (r.slug || '').toLowerCase().includes(q)
				|| (r.status || '').toLowerCase().includes(q)
			);
		this.filteredRows = base.sort((a, b) => this.byWallOrder(a, b));
	}

	onSearchChange(value: string): void {
		this.search = value;
		this.applyFilter();
	}

	statusLabel(row: any): string {
		if (!row || row.enabled != 1) {
			return 'Off';
		}
		return (row.status || 'off').charAt(0).toUpperCase() + (row.status || 'off').slice(1);
	}

	statusClass(row: any): string {
		if (!row || row.enabled != 1) {
			return 'badge-secondary';
		}
		if (row.status === 'active') {
			return 'badge-success';
		}
		if (row.status === 'paused') {
			return 'badge-warning';
		}
		return 'badge-danger';
	}

	selectRow(row: any, scroll = true): void {
		this.selected = row;
		this.copyHint = '';
		this.form.patchValue({
			manufacturer_id: row.manufacturer_id,
			enabled: row.enabled == 1,
			status: row.status || 'cancelled',
			monthly_fee: row.monthly_fee || 100,
			setup_fee: row.setup_fee || 0,
			bill_to_debtorno: row.bill_to_debtorno || '',
			bill_to_branchcode: row.bill_to_branchcode || '',
			slug: row.slug || '',
			contact_email: row.contact_email || '',
			website_url: row.website_url || '',
			welcome_text: row.welcome_text || '',
			hero_image: row.hero_image || '',
			banner_image: row.banner_image || '',
			promo_banner_text: row.promo_banner_text || '',
			promo_banner_link: row.promo_banner_link || '',
			accent_color: row.accent_color || '',
			notes: row.notes || '',
			invoice_setup_fee: !(row.setup_fee_invoiced == 1)
		});
		this.updateBillToValidators();
	}

	save(): void {
		this.updateBillToValidators();
		if (!this.form.valid) {
			this.showMessage('Fill in required fields before saving.', 'danger');
			return;
		}
		this.saving = true;
		this.message = '';
		const payload = { ...this.form.value, enabled: this.form.value.enabled ? 1 : 0 };
		this.brandStorefrontService.saveSubscription(payload).subscribe(() => {
			this.brandStorefrontService.saveConfig(payload).subscribe(() => {
				this.saving = false;
				this.showMessage('Brand storefront saved.', 'success');
				this.loadRows();
			}, (err: any) => {
				this.saving = false;
				// The API rejects a malformed contact email/website with 422 and a
				// per-field reason; show that instead of a generic failure, or the
				// user has no idea which field it objected to.
				const fieldErrors = err?.error?.errors;
				const detail = fieldErrors ? Object.keys(fieldErrors).map(k => fieldErrors[k]).join(' ') : '';
				this.showMessage(detail || 'Subscription saved but config failed.', 'danger');
			});
		}, () => {
			this.saving = false;
			this.showMessage('Unable to save subscription.', 'danger');
		});
	}

	copyLink(row: any): void {
		const url = row?.share_url || '';
		if (!url) {
			return;
		}
		if (navigator.clipboard) {
			navigator.clipboard.writeText(url).then(() => {
				this.copyHint = 'Link copied to clipboard.';
			});
		}
	}

	runBilling(): void {
		this.billing = true;
		this.brandStorefrontService.runBilling().subscribe((res: any) => {
			this.billing = false;
			const count = res?.count || 0;
			this.showMessage(count ? `Processed ${count} billing run(s).` : 'No subscriptions were due.', 'success');
			this.loadRows();
		}, () => {
			this.billing = false;
			this.showMessage('Billing run failed.', 'danger');
		});
	}

	showMessage(text: string, type: 'success' | 'danger'): void {
		this.message = text;
		this.messageType = type;
	}

	back(): void {
		this.location.back();
	}
}
